import React from 'react';
import { X, Sparkles, Download, Share2, ArrowRight, Star, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FirstClipSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  clipCount: number;
  videoTitle: string;
  videoId: string;
}

const FirstClipSuccessModal: React.FC<FirstClipSuccessModalProps> = ({
  isOpen,
  onClose,
  clipCount,
  videoTitle,
  videoId
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleViewClips = () => {
    onClose();
    navigate(`/videos/${videoId}`);
  };

  const handleUploadAnother = () => {
    onClose();
    navigate('/upload');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 animate-fade-up relative overflow-hidden">
          {/* Confetti background effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-2 h-2 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0s' }} />
          <div className="absolute top-10 right-1/4 w-2 h-2 bg-gray-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
          <div className="absolute top-5 left-1/3 w-2 h-2 bg-red-300 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <Sparkles className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold mb-2">🎉 Success!</h2>
            <p className="text-red-100 text-lg">
              Your first {clipCount} clip{clipCount !== 1 ? 's are' : ' is'} ready!
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <Star className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">From: {videoTitle}</div>
                  <div className="text-xs text-gray-600">{clipCount} clips generated</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="text-sm text-gray-700 mb-2">
                <strong>What our AI found:</strong>
              </p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>✨ Audio spikes and exciting moments</li>
                <li>😊 Facial expressions and reactions</li>
                <li>🎯 Perfect timing for social media</li>
              </ul>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <span className="text-lg mr-2">💡</span>
              Next Steps
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start">
                <Download className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Review and download your favorite clips</span>
              </div>
              <div className="flex items-start">
                <Share2 className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Share them on TikTok, Instagram, or YouTube Shorts</span>
              </div>
              <div className="flex items-start">
                <Sparkles className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>Upload another video to test AI consistency</span>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-red-800">
              <strong>🎯 Pro Tip:</strong> Upload 2-3 different videos to see how our AI adapts to different content types. Gaming, reactions, and tutorials each work differently!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleViewClips}
              className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-all duration-200 flex items-center justify-center"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              View My Clips
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            
            <button
              onClick={handleUploadAnother}
              className="w-full bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center justify-center border border-gray-300"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload Another Video
            </button>
          </div>

          {/* Reminder */}
          <p className="text-center text-xs text-gray-500 mt-4">
            You have 2 more videos remaining this month
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirstClipSuccessModal;

